import { useCallback } from "react";

import type { Editor } from "tinymce";

import useLatestRef from "hooks/use-latest-ref.hook";
import { useLazyGetMentionCandidatesQuery } from "services/mentions";
import {
  type MentionCandidate,
  isTeamCandidate
} from "services/types/mentions.type";

import {
  markMentionButtonUsed,
  markMentionsUsed
} from "./launch/mentions-launch";
import {
  MENTION_AUTOCOMPLETER_ID,
  MENTION_CONTENT_STYLE,
  MENTION_TOOLBAR_BUTTON,
  buildMentionHtml,
  buildTeamMentionHtml
} from "./mentions.util";

const MAX_RESULTS = 8;

/** "Team · 6 people · shows on everyone's Home, first to take it owns it" (TEAMS_FEATURE). */
export const describeTeamCandidate = (candidate: MentionCandidate) => {
  const count = candidate.member_count ?? 0;
  const people = count === 1 ? "1 person" : `${count} people`;
  return `Team · ${people} · shows on everyone's Home, first to take it owns it`;
};

export const describeCandidate = (candidate: MentionCandidate) => {
  if (isTeamCandidate(candidate)) return describeTeamCandidate(candidate);
  const clinics = (candidate.clinic_names ?? []).join(", ");
  // Allowed, but said out loud: they may not know this patient.
  const elsewhere = candidate.in_clinic ? "" : "Not at this clinic";
  return [candidate.role, clinics, elsewhere].filter(Boolean).join(" · ");
};

/** The autocompleter's item value: a person's id as before, a team prefixed so ids cannot collide. */
const candidateKey = (candidate: MentionCandidate) =>
  isTeamCandidate(candidate) ? `team:${candidate.id}` : String(candidate.id);

/** The pill the editor inserts for a picked person or team. */
export const buildCandidateHtml = (candidate: MentionCandidate) =>
  isTeamCandidate(candidate)
    ? buildTeamMentionHtml(candidate.id, candidate.name)
    : buildMentionHtml(candidate.id, candidate.name);

/**
 * The "@" picker inside a TinyMCE editor (`WordProcessorField`): chart notes and task descriptions.
 *
 * Returns props for `WordProcessorField` (`onEditorSetup`, `extraToolbarItems`,
 * `extraContentStyle`). All three are referentially stable, and anything that changes between
 * renders (the clinic, the fetch trigger) is read through refs. tinymce-react reads `init` only on
 * first mount, so whether the picker exists is decided when the editor mounts: turning the flag on
 * while a note is already open shows the Notify bar but not the picker until the note reopens.
 *
 * `contentStyle` is the pill CSS for the editor iframe: dashed-until-notified for chart notes
 * (default), solid (`MENTION_SOLID_CONTENT_STYLE`) where saving notifies. It must be a stable value
 * (a module constant), since it feeds the memoised editor `init`.
 *
 * TinyMCE only opens an autocompleter at the start of a word, so an email address
 * ("jo@clinic.com") or "@" in the middle of a word never opens the picker. The search text cannot
 * contain a space (TinyMCE stops looking at whitespace), so people type "@kar", not "@Karen R";
 * the backend matches each name by prefix.
 */
export default function useMentionEditor({
  enabled,
  clinicId,
  contentStyle = MENTION_CONTENT_STYLE,
  includeTeams = false
}: {
  enabled: boolean;
  clinicId?: number;
  contentStyle?: string;
  /** TEAMS_FEATURE: list the practice's teams before the people. Off, the request is as before. */
  includeTeams?: boolean;
}) {
  const [fetchCandidates] = useLazyGetMentionCandidatesQuery();
  const fetchRef = useLatestRef(fetchCandidates);
  const clinicIdRef = useLatestRef(clinicId);
  const includeTeamsRef = useLatestRef(includeTeams);

  const onEditorSetup = useCallback(
    (editor: Editor) => {
      // Names of the people in the last result, so onAction can insert the name for an id.
      const lastResults = new Map<string, MentionCandidate>();

      editor.ui.registry.addAutocompleter(MENTION_AUTOCOMPLETER_ID, {
        trigger: "@",
        minChars: 0,
        columns: 1,
        maxResults: MAX_RESULTS,
        highlightOn: ["mention_name"],
        fetch: async (pattern: string) => {
          try {
            const candidates = await fetchRef
              .current({
                search: pattern.trim(),
                clinic: clinicIdRef.current,
                limit: MAX_RESULTS,
                ...(includeTeamsRef.current ? { include_teams: true } : {})
              })
              .unwrap();
            lastResults.clear();
            return candidates.map((candidate) => {
              const key = candidateKey(candidate);
              lastResults.set(key, candidate);
              const subtitle = describeCandidate(candidate);
              return {
                type: "cardmenuitem" as const,
                value: key,
                label: candidate.name,
                items: [
                  {
                    type: "cardcontainer" as const,
                    direction: "vertical" as const,
                    items: [
                      {
                        type: "cardtext" as const,
                        text: candidate.name,
                        name: "mention_name",
                        classes: [
                          "suno-mention-option-name",
                          ...(isTeamCandidate(candidate)
                            ? ["suno-mention-option-team"]
                            : [])
                        ]
                      },
                      ...(subtitle
                        ? [
                            {
                              type: "cardtext" as const,
                              text: subtitle,
                              classes: ["suno-mention-option-role"]
                            }
                          ]
                        : [])
                    ]
                  }
                ]
              };
            });
          } catch {
            // A failed search shows no menu rather than an error mid-sentence.
            return [];
          }
        },
        onAction: (autocompleteApi, range, value) => {
          const candidate = lastResults.get(String(value));
          if (!candidate) {
            autocompleteApi.hide();
            return;
          }
          // Replace the typed "@kar" with the pill. Hide only AFTER inserting: hiding first ends the
          // autocomplete session and leaves `range` collapsed, so the search text stayed behind
          // ("@Karen Ruiz @kar").
          editor.selection.setRng(range);
          editor.insertContent(`${buildCandidateHtml(candidate)}&nbsp;`);
          autocompleteApi.hide();
          markMentionsUsed();
        }
      });

      // For people who don't know the shortcut: types "@" at the caret (after a space when needed,
      // since the picker only opens at the start of a word) and opens the picker.
      editor.ui.registry.addButton(MENTION_TOOLBAR_BUTTON, {
        text: "@",
        tooltip: "Mention a teammate",
        onAction: () => {
          markMentionButtonUsed();
          editor.focus();
          const range = editor.selection.getRng();
          let needsSpace = false;
          if (
            range.collapsed &&
            range.startContainer.nodeType === Node.TEXT_NODE
          ) {
            const textBefore = (range.startContainer.textContent ?? "").slice(
              0,
              range.startOffset
            );
            const previous = textBefore.slice(-1);
            needsSpace = previous !== "" && !/\s| /.test(previous);
          }
          editor.insertContent(needsSpace ? "&nbsp;@" : "@");
          editor.execCommand("mceAutocompleterReload");
        }
      });
      // Stable refs: the setup callback must stay stable for WordProcessorField.
    },
    [clinicIdRef, fetchRef, includeTeamsRef]
  );

  return enabled
    ? {
        onEditorSetup,
        extraToolbarItems: MENTION_TOOLBAR_BUTTON,
        extraContentStyle: contentStyle
      }
    : {};
}
